import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, ReceiptText } from "lucide-react";

import {
  Badge,
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  DataTable,
  type DataTableColumn,
  EmptyState,
  Eyebrow,
  FormField,
  H2,
  Input,
  Label,
  Mono,
  PageHeader,
  Progress,
  SelectItem,
  Text,
} from "@lagoon/ui";

import {
  api,
  ApiError,
  type BillingCheckoutConfig,
  type BillingPeriod,
  type BillingSummary,
  type Invoice,
  type ProrationPreview,
} from "../api.js";
import {
  canCheckout,
  canManageBilling,
  canSeeBilling,
  canUpdateCard,
  CHECKOUT_FIELDS,
  describeCheckoutAmount,
  describePlanOption,
  describeProration,
  NO_VALUE,
  planDirection,
  readMeters,
  selectablePlans,
  tokeniserConfig,
  trialCountdown,
  type MeterReading,
} from "../billing.js";
import { browserScriptHost, ensureCheckoutScript } from "../checkout.js";
import { DunningBanner } from "../components/dunning-banner.js";
import { Failure, LoadingPanel, Notice, Working } from "../components/feedback.js";
import { SelectField } from "../components/select-field.js";
import type { Org } from "../orgs.js";

/**
 * Billing: the plan, the meters, the card, the invoices, and the way out.
 *
 * Four properties shape everything below, and each is a decision worth naming
 * rather than a style choice.
 *
 * **The card never touches this bundle.** The fields on the form are iframes
 * served from the payment provider's origin, drawn by its script; what this
 * page ever holds is a one-time token. That is enforced by the dashboard's CSP
 * rather than by intention, and the script is not even fetched unless the
 * platform said there is something to initialise it with (`checkout.ts`).
 *
 * **A write is a request.** Every mutation here answers 202: the plan moves
 * when the webhook lands and the inbox worker applies it, which is the single
 * writer plan CD4 asks for. So the page says "asked for, confirming" and
 * refetches on a short poll; it never paints the new plan as done.
 *
 * **Unconfigured is a normal state, not a failure.** dev2 holds no credentials.
 * The meters still come from entitlements rather than from the provider, so
 * they are shown, and the card form is replaced by a sentence saying why there
 * is not one.
 *
 * **The meters cite the numbers the refusals cite.** Somebody sent here by
 * "you are out of sites" finds the same integer they were just quoted.
 *
 * The design library re-skin (LGN-108) changed none of that. The checkout
 * containers keep the ids `checkout-number` and friends, the script is still a
 * classic tag added by `ensureCheckoutScript` and only when there is a config,
 * `tokeniserConfig` still carries `gatewayHandle` and `threeDSecure`, and the
 * CSP is untouched. Everything around the four iframes is what moved.
 */
export function Billing(props: {
  org: Org;
  navigate: (to: string) => void;
  supportEmail: string;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const orgId = props.org.id;

  /** The plan the customer last asked for, whether or not it has landed. */
  const [requestedPlan, setRequestedPlan] = useState<string | null>(null);

  /**
   * Refetched on a timer only while something is pending confirmation.
   *
   * The read costs a handful of indexed queries and never a provider call, so
   * polling it is cheap; polling it forever would still be a request every few
   * seconds from every open dashboard tab for no reason.
   */
  const billing = useQuery({
    queryKey: ["billing", orgId],
    queryFn: () => api.getBilling(orgId),
    refetchInterval: requestedPlan === null ? false : 5000,
  });

  const invoices = useQuery({
    queryKey: ["billing-invoices", orgId],
    queryFn: async () => (await api.getInvoices(orgId)).invoices,
    // The route reaches the provider, so it is asked once per visit rather than
    // on the summary's poll.
    enabled: billing.isSuccess,
  });

  const summary = billing.data;

  /**
   * Still waiting on the webhook, derived rather than stored.
   *
   * The fact is a comparison between the plan asked for and the plan in force,
   * so it is computed during render: a second piece of state cleared from an
   * effect would be the same comparison, one render later, with a chance of
   * being wrong in between. Keyed on the plan code rather than on elapsed time,
   * because the wait is for a specific fact and "it has probably landed by now"
   * is not that fact.
   */
  const awaitingWebhook =
    requestedPlan !== null && summary?.plan.code !== requestedPlan ? requestedPlan : null;

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ["billing", orgId] });
    await queryClient.invalidateQueries({ queryKey: ["billing-invoices", orgId] });
    // The plan moves entitlements, which the sites screen, the switcher and the
    // shell's dunning banner all read.
    await queryClient.invalidateQueries({ queryKey: ["entitlements", orgId] });
    await queryClient.invalidateQueries({ queryKey: ["orgs"] });
  };

  /**
   * The role refusal, in front of the server's own.
   *
   * Not a courtesy any more (LGN-97): `GET /api/billing` and its invoices
   * answer 403 to an admin or a member, so without this the same person would
   * meet a red error box instead of a sentence explaining who to ask. The set
   * here and `BILLING_ROLES` on the platform are the same rule stated twice on
   * purpose, and the platform's is the one that enforces it.
   */
  if (!canSeeBilling(props.org.role)) {
    return (
      <div className="lg:flex lg:max-w-3xl lg:flex-col lg:gap-6">
        <PageHeader eyebrow={props.org.name} title="Billing" />
        <Notice tone="neutral">
          Billing is looked after by this organisation&rsquo;s owner and its billing contact. Ask
          one of them if you need an invoice or a change of plan.
        </Notice>
      </div>
    );
  }

  if (billing.isPending) {
    return <LoadingPanel label="Loading billing" />;
  }

  if (billing.isError || summary === undefined) {
    return (
      <Failure error={billing.error} fallback="Could not load billing for this organisation." />
    );
  }

  /** Starting a subscription, changing the plan, cancelling: `owner` alone. */
  const manage = canManageBilling(summary.role);
  /**
   * Replacing the card, which a `billing` contact may also do once there is a
   * subscription to replace it on. The same condition the platform applies, so
   * the form is offered exactly when the write behind it would be accepted.
   */
  const card = canUpdateCard(summary.role, summary.subscription !== null);

  return (
    <div className="lg:flex lg:max-w-4xl lg:flex-col lg:gap-6">
      <PageHeader eyebrow={props.org.name} title="Billing" />

      {/* The shell shows this too. Here it repeats without the link, because
          the link would point at the page the reader is already on. */}
      <DunningBanner dunning={summary.dunning} showLink={false} navigate={props.navigate} />

      {summary.configured ? null : (
        <Notice tone="neutral">
          <strong className="lg:text-strong">Billing is not connected in this environment.</strong>{" "}
          The plan and the usage below are real and are what the limits are enforced against. Cards,
          invoices and plan changes need the payment provider, which this deployment has no
          credentials for.
        </Notice>
      )}

      <PlanCard summary={summary} awaiting={awaitingWebhook} />

      <Meters readings={readMeters(summary.meters)} />

      {card && canCheckout(summary) && summary.checkout !== null ? (
        <CardForm
          checkout={summary.checkout}
          summary={summary}
          onDone={async (requested) => {
            if (requested !== null) setRequestedPlan(requested);
            await refresh();
          }}
        />
      ) : card && summary.configured ? (
        <Card as="section" interactive={false}>
          <CardHeader>
            <CardTitle as="h2">Payment method</CardTitle>
            <CardDescription>
              The card form is not available yet: this site has no publishable key for the hosted
              fields. Ask <a href={`mailto:${props.supportEmail}`}>{props.supportEmail}</a> and we
              will take the details a safer way.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : null}

      {manage ? (
        <ChangePlan
          summary={summary}
          onRequested={async (plan) => {
            setRequestedPlan(plan);
            await refresh();
          }}
        />
      ) : null}

      <Invoices query={invoices} configured={summary.configured} />

      {manage && summary.subscription !== null ? (
        <CancelSubscription orgId={orgId} onCancelled={refresh} />
      ) : null}
    </div>
  );
}

// --- the plan ----------------------------------------------------------------

/**
 * What the organisation is on, and what is about to change about it.
 *
 * The trial countdown is here rather than in the banner because a trial is not
 * a problem: it is the state a new customer is meant to be in, and putting it
 * on every screen in a coloured box would teach them to ignore coloured boxes.
 * The last three days are the exception, and the shell does not show that
 * either; the countdown presses on this page instead.
 */
function PlanCard(props: { summary: BillingSummary; awaiting: string | null }): React.ReactElement {
  const trial = trialCountdown(props.summary.subscription?.trialEndsAt ?? null, new Date());

  return (
    <Card as="section" interactive={false}>
      <div className="lg:flex lg:flex-col lg:gap-4">
        <div className="lg:flex lg:flex-wrap lg:items-start lg:justify-between lg:gap-4">
          <div>
            <Eyebrow>Current plan</Eyebrow>
            <H2 className="lg:mt-2 lg:text-h3">{props.summary.plan.name}</H2>
          </div>
          {props.summary.subscription === null ? (
            <Badge tone="neutral">No subscription</Badge>
          ) : (
            <Badge tone={props.summary.dunning.createBlocked ? "danger" : "success"}>
              {props.summary.dunning.subscriptionState}
            </Badge>
          )}
        </div>

        {trial === null ? null : trial.expired ? (
          <Text size="caption" tone="subtle">
            The trial has ended. Add a card to keep the paid entitlements.
          </Text>
        ) : trial.ending ? (
          <Notice tone="warning">
            {trial.daysLeft === 0
              ? "The trial ends today."
              : `${trial.daysLeft} ${trial.daysLeft === 1 ? "day" : "days"} left on the trial.`}{" "}
            Adding a card now changes nothing until it ends.
          </Notice>
        ) : (
          <Text size="caption" tone="subtle">
            {trial.daysLeft === 0
              ? "The trial ends today."
              : `${trial.daysLeft} ${trial.daysLeft === 1 ? "day" : "days"} left on the trial.`}{" "}
            Adding a card now changes nothing until it ends.
          </Text>
        )}

        {props.awaiting === null ? null : (
          // Deliberate wording. The payment provider has accepted the
          // instruction; the plan on this org moves when its webhook lands and
          // the worker applies it, which is the only writer there is.
          <Working label="Change requested, confirming with the payment provider. This page updates itself." />
        )}
      </div>
    </Card>
  );
}

// --- the meters --------------------------------------------------------------

function Meters(props: { readings: readonly MeterReading[] }): React.ReactElement {
  return (
    <Card as="section" interactive={false}>
      <div className="lg:flex lg:flex-col lg:gap-5">
        <CardHeader>
          <CardTitle as="h2">Usage</CardTitle>
          <CardDescription>
            These are the numbers the limits are enforced against, not a separate count.
          </CardDescription>
        </CardHeader>
        <div className="lg:flex lg:flex-col lg:gap-4">
          {props.readings.map((reading) => (
            <Meter key={reading.label} reading={reading} />
          ))}
        </div>
      </div>
    </Card>
  );
}

/**
 * One meter.
 *
 * The bar was a real `<progress>` with three bespoke rules for its two vendor
 * pseudo-elements. It is the library's `Progress` now, which is Radix and
 * therefore carries `role="progressbar"` and the aria value attributes itself:
 * a screen reader still reads "42 percent" with no aria plumbing of ours. What
 * the library does not carry is a tone, so the two warning colours are applied
 * to the indicator slot, still as `--warning` and `--destructive` tokens rather
 * than as literals.
 */
function Meter(props: { reading: MeterReading }): React.ReactElement {
  const r = props.reading;
  return (
    <div className="lg:flex lg:flex-col lg:gap-2">
      <div className="lg:flex lg:items-baseline lg:justify-between lg:gap-4">
        <span className="lg:text-body-sm">{r.label}</span>
        <Mono
          className={
            r.exceeded ? "lg:text-caption lg:text-warning" : "lg:text-caption lg:text-subtle"
          }
        >
          {r.limit === null ? r.used : `${r.used} of ${r.limit}`}
        </Mono>
      </div>
      {r.percent === null ? null : (
        <Progress
          value={r.percent}
          aria-label={`${r.label} used`}
          className={
            r.exceeded
              ? "lg:[&_[data-slot=progress-indicator]]:bg-destructive"
              : r.near
                ? "lg:[&_[data-slot=progress-indicator]]:bg-warning"
                : "lg:[&_[data-slot=progress-indicator]]:bg-success"
          }
        />
      )}
      {r.note === null ? null : (
        <Text as="span" size="caption" tone="subtle">
          {r.note}
        </Text>
      )}
      {r.exceeded ? (
        <Text as="span" size="caption" tone="subtle">
          At the limit. The next one will be refused until the plan changes.
        </Text>
      ) : null}
    </div>
  );
}

// --- the card ----------------------------------------------------------------

/**
 * The payment provider's tokeniser, as this page uses it.
 *
 * Typed to the three calls that are made and nothing else. A fuller declaration
 * would be a second, unversioned copy of somebody else's API living in our
 * repository, going stale silently.
 */
interface Tokeniser {
  load(config: Record<string, unknown>): void;
  token(
    form: HTMLFormElement,
    onSuccess: (token: string) => void,
    onError: (error: unknown) => void,
  ): void;
  unload?: () => void;
}

type TokeniserConstructor = new () => Tokeniser;

/**
 * The global the provider's script defines, declared rather than asserted.
 *
 * `globalThis as { Chargify?: … }` would compile and would keep compiling if
 * the shape were wrong. Declaring it once means the two places that read it are
 * type-checked against the same three-method interface above, and neither needs
 * a cast.
 */
declare global {
  // eslint-disable-next-line no-var
  var Chargify: TokeniserConstructor | undefined;
}

/**
 * Enter a card, or replace the one on file.
 *
 * One gesture, two outcomes, decided by whether the organisation already has a
 * subscription. The platform makes the same split for the same reason: asking
 * the dashboard to decide would mean keeping a second copy of that state in
 * sync with the one the server already has.
 */
function CardForm(props: {
  checkout: BillingCheckoutConfig;
  summary: BillingSummary;
  onDone: (requestedPlan: string | null) => Promise<void>;
}): React.ReactElement {
  const form = useRef<HTMLFormElement>(null);
  const tokeniser = useRef<Tokeniser | null>(null);
  const [ready, setReady] = useState(false);
  const [scriptFailed, setScriptFailed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const buyable = props.summary.plans.filter((plan) => plan.available);
  /**
   * The plan that will be sent, which is not simply the one in the state
   * (LGN-117).
   *
   * `useState(() => buyable[0]?.code ?? "")` read the list once, on the first
   * render, and a first render can happen before there is a list: a plan whose
   * `maxio_product_handle` is null is not buyable, which is every plan until
   * the catalog is created, and the billing query refetches. Somebody with the
   * screen open across that moment kept `""`, saw a select with options and no
   * selection, and got a 400 from a form that looked filled in.
   *
   * Derived on every render rather than repaired in an effect. The state holds
   * only what the person actually chose, and a choice that is not in the
   * current list, because it was never made or because the catalog changed
   * under it, falls back to the first plan that can be bought. An effect would
   * reach the same place one render later, through a second render nobody
   * needs, and would still be wrong for the render that submitted.
   */
  const [chosen, setChosen] = useState<string>("");
  const plan = buyable.some((option) => option.code === chosen) ? chosen : (buyable[0]?.code ?? "");
  const [period, setPeriod] = useState<BillingPeriod>("monthly");
  const existing = props.summary.subscription !== null;

  /**
   * What this card is about to be committed to (LGN-127).
   *
   * Derived from the plan and the period on every render, exactly as `plan`
   * itself is, so the sentence and the amount can never describe a different
   * choice from the one the form would submit. `known` is false when the
   * catalog has no price for the pair, and it disables the button: the audit's
   * H4 is that a payment form must show the amount at the point of commitment,
   * and the honest answer to "we cannot" is to stop asking for the card rather
   * than to print a plausible number.
   *
   * Rendered only on the purchase path. Replacing a card buys nothing, and the
   * plan already on the subscription is not this form's to restate.
   */
  const amount = describeCheckoutAmount(
    buyable.find((option) => option.code === plan),
    period,
  );
  /** Whether the sentence above is on the page for `aria-describedby` to name. */
  const showAmount = !existing && buyable.length > 0;

  const { publicKey, serverHost, scriptUrl, gatewayHandle } = props.checkout;

  /**
   * Load the script, then draw the fields into it.
   *
   * Two steps rather than one because the second cannot happen until the first
   * has: `load` is a method on a global the script defines. The effect is keyed
   * on the three configuration strings, so a redeploy that changes the site
   * host redraws the fields rather than leaving them pointed at the old one.
   */
  useEffect(() => {
    let cancelled = false;
    const cleanup = (): void => {
      cancelled = true;
      tokeniser.current?.unload?.();
      tokeniser.current = null;
    };

    const host = browserScriptHost();
    if (host === null) return cleanup;

    const script = ensureCheckoutScript(host, { publicKey, serverHost, scriptUrl, gatewayHandle });
    // Null is the refusal in `checkout.ts`, and it is unreachable from here
    // because this component only renders when there is a config. Handled
    // anyway: an unreachable branch that returns is cheaper than one that
    // throws in front of a customer.
    if (script === null) return cleanup;

    const draw = (): void => {
      if (cancelled) return;
      const Constructor = globalThis.Chargify;
      if (Constructor === undefined) {
        setScriptFailed(true);
        return;
      }
      const instance = new Constructor();
      instance.load(tokeniserConfig({ publicKey, serverHost, gatewayHandle }));
      tokeniser.current = instance;
      setReady(true);
    };

    // Already there from an earlier visit to this screen: the script is
    // idempotent by marker attribute, but its `load` event has long since
    // fired, so waiting for it would wait forever.
    if (globalThis.Chargify !== undefined) {
      draw();
    } else {
      script.addEventListener("load", draw);
      script.addEventListener("error", () => {
        if (!cancelled) setScriptFailed(true);
      });
    }

    return cleanup;
  }, [publicKey, serverHost, scriptUrl, gatewayHandle]);

  const submit = useMutation({
    mutationFn: async (): Promise<string | null> => {
      const element = form.current;
      const instance = tokeniser.current;
      if (element === null || instance === null) throw new Error("The card form is not ready.");

      const token = await new Promise<string>((resolve, reject) => {
        instance.token(
          element,
          (value) => {
            resolve(value);
          },
          (caught) => {
            reject(new Error(readTokenError(caught)));
          },
        );
      });

      const answer = await api.checkout({
        orgId: props.summary.orgId,
        token,
        period,
        ...(existing ? {} : { plan }),
      });
      return answer.requestedPlan ?? null;
    },
    onSuccess: async (requested) => {
      setError(null);
      await props.onDone(requested);
    },
    onError: (caught) => {
      setError(
        caught instanceof ApiError
          ? caught.message
          : caught instanceof Error
            ? caught.message
            : "Could not save the card.",
      );
    },
  });

  return (
    <Card as="section" interactive={false}>
      <div className="lg:flex lg:flex-col lg:gap-5">
        <CardHeader>
          <CardTitle as="h2">{existing ? "Payment method" : "Add a card"}</CardTitle>
          <CardDescription>
            {/* Not marketing copy: it is a true statement about where the bytes
                go, and it is the reason the fields look slightly different from
                the rest of the form. */}
            The card fields below are served by the payment provider and the number never reaches
            this service.
          </CardDescription>
        </CardHeader>

        {scriptFailed ? (
          <Notice tone="danger">
            The secure card form could not be loaded. If an extension or a network filter is
            blocking the payment provider, allow it and reload.
          </Notice>
        ) : null}

        {error === null ? null : <Notice tone="danger">{error}</Notice>}

        <form
          ref={form}
          onSubmit={(event) => {
            event.preventDefault();
            submit.mutate();
          }}
          className="lg:flex lg:flex-col lg:gap-5"
        >
          {existing ? null : (
            <div className="lg:flex lg:flex-wrap lg:gap-4">
              <SelectField
                id="checkout-plan"
                label="Plan"
                className="lg:min-w-48 lg:flex-1"
                value={plan}
                onValueChange={(value) => {
                  setChosen(value);
                }}
              >
                {/*
                  The price is in the option's own text, not in a column beside
                  it: a native select renders one string per option, and a plan
                  named without a number reads as free (LGN-127).
                */}
                {buyable.map((option) => (
                  <SelectItem key={option.code} value={option.code}>
                    {describePlanOption(option)}
                  </SelectItem>
                ))}
              </SelectField>
              <SelectField
                id="checkout-period"
                label="Billed"
                className="lg:w-44"
                value={period}
                onValueChange={(value) => {
                  setPeriod(value === "annual" ? "annual" : "monthly");
                }}
              >
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="annual">Annually</SelectItem>
              </SelectField>
            </div>
          )}

          {/*
            The hosted fields.

            Everything about this block is load bearing and none of it moved in
            the re-skin: `CHECKOUT_FIELDS` is the single list, each container
            keeps its id (`checkout-number`, `checkout-month`, `checkout-year`,
            `checkout-cvv`), and each `<div>` is EMPTY on purpose because the
            provider's script replaces its contents with an iframe from its own
            origin. Putting anything inside one, including a placeholder or a
            skeleton, is how the card form silently stops rendering.

            The wrapper is the library's `Label`, which renders the same
            `<label>` element the hand-rolled version did, in the brand's field
            voice. It has no `htmlFor` because there is no control of ours to
            point at: the input lives in another document.
          */}
          <div className="lg:grid lg:gap-4 lg:sm:grid-cols-2">
            {CHECKOUT_FIELDS.map((field) => (
              <div key={field.key} className="lg:flex lg:flex-col lg:gap-2">
                <Label>{field.label}</Label>
                <div id={field.id} />
              </div>
            ))}
          </div>

          {/*
            The amount, immediately above the button that commits to it.

            Rendered before the button rather than after it so that it is read
            on the way to pressing, and so that a screen reader reaches it
            first. `aria-describedby` ties the two together for the case where
            it is not.
          */}
          {showAmount ? (
            <Text id="checkout-amount" size="sm" tone={amount.known ? "default" : "danger"}>
              {amount.text}
            </Text>
          ) : null}

          <div className="lg:flex lg:flex-wrap lg:items-center lg:gap-4">
            <Button
              type="submit"
              aria-describedby={showAmount ? "checkout-amount" : undefined}
              disabled={
                !ready ||
                submit.isPending ||
                // Nothing to buy, or nothing we can honestly say it costs. The
                // second is the LGN-127 gate: no price, no card.
                (!existing && (buyable.length === 0 || !amount.known))
              }
            >
              {submit.isPending ? "Saving…" : existing ? "Replace card" : "Subscribe"}
            </Button>
            {ready || scriptFailed ? null : <Working label="Loading the secure card fields…" />}
          </div>

          {existing || buyable.length > 0 ? null : (
            <Text size="caption" tone="subtle">
              No plan can be bought in this environment yet, so there is nothing to subscribe to.
            </Text>
          )}
        </form>
      </div>
    </Card>
  );
}

/**
 * A tokeniser failure, in words.
 *
 * The provider's error object is a shape we do not control, so it is read
 * defensively and falls back to our own sentence rather than to `[object
 * Object]`. It never carries card data: by construction it cannot, since the
 * data was never in this frame.
 */
function readTokenError(caught: unknown): string {
  if (typeof caught === "string" && caught.length > 0) return caught;
  if (typeof caught === "object" && caught !== null) {
    const message: unknown = Reflect.get(caught, "errors") ?? Reflect.get(caught, "message");
    if (typeof message === "string" && message.length > 0) return message;
  }
  return "The card details were not accepted. Check them and try again.";
}

// --- changing plan -----------------------------------------------------------

/**
 * Upgrade or downgrade, with the provider's own proration first.
 *
 * The preview is a separate button and a separate call because it changes
 * nothing, and because the number it returns is the provider's arithmetic
 * rather than ours. It is deliberately not repeated on the apply path: two
 * calls either side of a period boundary would show one number and charge
 * another, and the customer has already agreed to the one they saw.
 */
function ChangePlan(props: {
  summary: BillingSummary;
  onRequested: (plan: string) => Promise<void>;
}): React.ReactElement | null {
  const options = selectablePlans(props.summary.plans);
  const [chosen, setChosen] = useState<string>(() => options[0]?.code ?? "");
  const [period, setPeriod] = useState<BillingPeriod>("monthly");
  const [preview, setPreview] = useState<ProrationPreview | null>(null);
  const [error, setError] = useState<string | null>(null);

  const target = options.find((option) => option.code === chosen);
  const current = props.summary.plans.find((option) => option.current);
  const direction = target === undefined ? "same" : planDirection(current, target);
  // The provider is only reachable with a subscription to migrate. Without one
  // the way onto a plan is the card form above, not this section.
  const usable =
    props.summary.configured && props.summary.subscription !== null && (target?.available ?? false);

  const previewMutation = useMutation({
    mutationFn: () => api.previewPlan({ orgId: props.summary.orgId, plan: chosen, period }),
    onSuccess: (answer) => {
      setError(null);
      setPreview(answer.preview);
    },
    onError: (caught) => {
      setError(failureText(caught, "Could not price that change."));
    },
  });

  const changeMutation = useMutation({
    mutationFn: () => api.changePlan({ orgId: props.summary.orgId, plan: chosen, period }),
    onSuccess: async (answer) => {
      setError(null);
      setPreview(null);
      await props.onRequested(answer.requestedPlan);
    },
    onError: (caught) => {
      setError(failureText(caught, "Could not change the plan."));
    },
  });

  // After the hooks, never before: a component that returns early above a
  // `useMutation` calls its hooks in a different order on the next render.
  if (options.length === 0) return null;

  return (
    <Card as="section" interactive={false}>
      <div className="lg:flex lg:flex-col lg:gap-5">
        <CardHeader>
          <CardTitle as="h2">Change plan</CardTitle>
          <CardDescription>
            {props.summary.subscription === null
              ? "Add a card above to move onto a paid plan."
              : "See what it costs before you commit to it."}
          </CardDescription>
        </CardHeader>

        {error === null ? null : <Notice tone="danger">{error}</Notice>}

        <div className="lg:flex lg:flex-wrap lg:gap-4">
          <SelectField
            id="change-plan"
            label="Plan"
            className="lg:min-w-48 lg:flex-1"
            value={chosen}
            onValueChange={(value) => {
              setChosen(value);
              // The old number is about a different plan. Keeping it on screen
              // beside a new choice is how somebody agrees to the wrong price.
              setPreview(null);
            }}
          >
            {options.map((option) => (
              <SelectItem key={option.code} value={option.code}>
                {/* The same labels the purchase picker uses: a plan the
                    customer is being asked to move onto should read the same
                    way in both places, price included (LGN-127). */}
                {describePlanOption(option)}
                {option.available
                  ? ""
                  : option.selfServe
                    ? " (not available yet)"
                    : " (talk to us)"}
              </SelectItem>
            ))}
          </SelectField>
          <SelectField
            id="change-period"
            label="Billed"
            className="lg:w-44"
            value={period}
            onValueChange={(value) => {
              setPeriod(value === "annual" ? "annual" : "monthly");
              setPreview(null);
            }}
          >
            <SelectItem value="monthly">Monthly</SelectItem>
            <SelectItem value="annual">Annually</SelectItem>
          </SelectField>
        </div>

        {direction === "same" ? null : (
          <Text size="caption" tone="subtle">
            {direction === "upgrade"
              ? "An upgrade takes effect immediately and is prorated for the rest of this period."
              : "A downgrade keeps the entitlements you have paid for until the period ends."}
          </Text>
        )}

        {preview === null ? null : <Notice tone="success">{describeProration(preview)}</Notice>}

        <div className="lg:flex lg:flex-wrap lg:gap-3">
          <Button
            type="button"
            variant="outline"
            disabled={!usable || previewMutation.isPending}
            onClick={() => {
              previewMutation.mutate();
            }}
          >
            {previewMutation.isPending ? "Pricing…" : "Preview the cost"}
          </Button>
          <Button
            type="button"
            // Preview first, on purpose: this is the only button on the page
            // that takes money, and it should not be reachable without the
            // customer having been shown the number.
            disabled={!usable || preview === null || changeMutation.isPending}
            onClick={() => {
              changeMutation.mutate();
            }}
          >
            {changeMutation.isPending ? "Requesting…" : "Change plan"}
          </Button>
        </div>

        {usable ? null : (
          <Text size="caption" tone="subtle">
            {props.summary.configured
              ? "This plan cannot be selected here yet."
              : "Plan changes need the payment provider, which this deployment is not connected to."}
          </Text>
        )}
      </div>
    </Card>
  );
}

// --- invoices ----------------------------------------------------------------

function Invoices(props: {
  query: { isPending: boolean; isError: boolean; error: unknown; data: Invoice[] | undefined };
  configured: boolean;
}): React.ReactElement {
  const columns = useMemo(() => invoiceColumns(), []);

  return (
    <section className="lg:flex lg:flex-col lg:gap-4">
      <H2 className="lg:text-h3">Invoices</H2>

      {props.query.isError ? (
        <Failure error={props.query.error} fallback="Could not load invoices." />
      ) : (
        <DataTable
          caption="Invoices for this organisation"
          columns={columns}
          data={props.query.data ?? []}
          loading={props.query.isPending}
          loadingRows={3}
          empty={
            <EmptyState
              glow={false}
              icon={<ReceiptText />}
              title="No invoices yet"
              description={
                props.configured
                  ? "One appears here after the first payment."
                  : "Invoices live with the payment provider, which this deployment is not connected to."
              }
            />
          }
        />
      )}
    </section>
  );
}

// --- cancelling --------------------------------------------------------------

/**
 * The way out, and it is deliberately not hidden.
 *
 * End of period rather than immediate: a customer who has paid for the month
 * keeps the month, and their sites keep serving until it runs out. The survey
 * is optional, because a required reason on a cancellation form is a dark
 * pattern, and the field says so rather than leaving it to be inferred.
 */
function CancelSubscription(props: {
  orgId: string;
  onCancelled: () => Promise<void>;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const cancel = useMutation({
    mutationFn: () =>
      api.cancelSubscription({
        orgId: props.orgId,
        ...(reason.trim().length > 0 ? { reason: reason.trim() } : {}),
      }),
    onSuccess: async (answer) => {
      setError(null);
      setOpen(false);
      setDone(answer.periodEndsAt);
      await props.onCancelled();
    },
    onError: (caught) => {
      setError(failureText(caught, "Could not cancel the subscription."));
    },
  });

  if (done !== null) {
    return (
      <Card as="section" interactive={false}>
        <div className="lg:flex lg:flex-col lg:gap-4">
          <CardTitle as="h2">Subscription</CardTitle>
          <Notice tone="success">
            Cancellation requested. The subscription runs to the end of the period it is paid for
            {done === "" ? "" : ` (${done})`}, and sites keep serving until then.
          </Notice>
        </div>
      </Card>
    );
  }

  return (
    <Card as="section" interactive={false}>
      <div className="lg:flex lg:flex-col lg:gap-4">
        <CardTitle as="h2">Cancel</CardTitle>
        {error === null ? null : <Notice tone="danger">{error}</Notice>}

        {open ? (
          <form
            className="lg:flex lg:flex-col lg:gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              cancel.mutate();
            }}
          >
            <Text size="sm" tone="muted">
              The subscription runs to the end of the period you have paid for. Sites stay online
              until then, and nothing is deleted when it ends.
            </Text>
            <FormField
              label="Why are you leaving? (optional)"
              id="cancel-reason"
              hint="Anything you tell us here goes to the team, not to a form."
            >
              <Input
                type="text"
                maxLength={500}
                value={reason}
                placeholder="Anything you tell us here goes to the team, not to a form"
                onChange={(event) => {
                  setReason(event.target.value);
                }}
              />
            </FormField>
            <div className="lg:flex lg:flex-wrap lg:gap-3">
              <Button type="submit" variant="destructive" disabled={cancel.isPending}>
                {cancel.isPending ? "Cancelling…" : "Cancel at period end"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setOpen(false);
                }}
              >
                Keep it
              </Button>
            </div>
          </form>
        ) : (
          <div className="lg:flex">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setOpen(true);
              }}
            >
              Cancel the subscription
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}

/** An API refusal in the words the platform chose, or the caller's fallback. */
function failureText(caught: unknown, fallback: string): string {
  return caught instanceof ApiError ? caught.message : fallback;
}

/**
 * The invoice columns, at module scope.
 *
 * A `cell` renderer returns JSX, so defining these inside the component reads
 * as a nested component to a linter and rebuilds the definitions on every
 * poll of the billing summary. They close over nothing, so this is a plain
 * function.
 */
function invoiceColumns(): DataTableColumn<Invoice>[] {
  return [
    {
      accessorKey: "issueDate",
      header: "Issued",
      sortFn: "datetime",
      cell: (info) => info.row.original.issueDate ?? NO_VALUE,
    },
    {
      accessorKey: "number",
      header: "Number",
      cell: (info) => <Mono>{info.row.original.number ?? info.row.original.uid}</Mono>,
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: (info) => info.row.original.status ?? NO_VALUE,
    },
    {
      accessorKey: "totalAmount",
      header: "Total",
      cell: (info) => {
        const invoice = info.row.original;
        return (
          <Mono>
            {invoice.totalAmount === null
              ? NO_VALUE
              : `${invoice.totalAmount} ${invoice.currency ?? ""}`.trim()}
          </Mono>
        );
      },
    },
    {
      id: "document",
      header: "",
      enableSorting: false,
      cell: (info) =>
        info.row.original.publicUrl === null ? null : (
          <span className="lg:flex lg:justify-end">
            <a
              href={info.row.original.publicUrl}
              // The provider's own invoice page, which is where the PDF is
              // downloaded from. A new tab, and `noopener` because the target
              // is not ours to hand a window reference to.
              target="_blank"
              rel="noopener noreferrer"
              className="lg:inline-flex lg:items-center lg:gap-1.5"
            >
              View
              <ExternalLink aria-hidden="true" className="lg:size-3.5" />
            </a>
          </span>
        ),
    },
  ];
}
